/**
 * Lógica pura de las adjudicaciones de unidades a los clase B (`docs/modulo-adjudicaciones.md`). El % nuevo, lo
 * atrasado y la distribución nueva los calcula el backend; acá se arma el formulario (quién cede o recibe y cuánto)
 * y los textos. Porcentajes escalados a 10⁴ (nunca float), como en participación.
 */
import type {
  ClaseFiduciante,
  Distribucion,
  Unidad,
  VistaPreviaAdjudicacion,
} from "./api";
import { formatFecha, formatMoneda } from "./format";
import { aEscalado, aTextoApi, formatParticipacion } from "./participacion";
import { nombreUnidad } from "./unidades";

export interface Contraparte {
  fiduciante_id: number;
  clase: ClaseFiduciante;
  nombre: string;
  porcentaje: string; // como lo escribió el admin ("0,18")
}

export interface FormAdjudicacion {
  fiduciante_id: number | null;
  unidad_ids: number[];
  fecha: string; // ISO
  contrapartes: Contraparte[];
  nota: string;
}

export interface PayloadAdjudicacion {
  fiduciante_id: number;
  unidad_ids: number[];
  fecha: string;
  contrapartes: {
    fiduciante_id: number;
    clase: ClaseFiduciante;
    porcentaje: string;
  }[];
  nota: string | null;
}

export const formAdjudicacion = (fecha: string): FormAdjudicacion => ({
  fiduciante_id: null,
  unidad_ids: [],
  fecha,
  contrapartes: [],
  nota: "",
});

/** Los clase B de la distribución vigente: a quiénes se les puede adjudicar. */
export function opcionesClaseB(
  vigente: Distribucion | null,
): { valor: string; etiqueta: string }[] {
  return (vigente?.items ?? [])
    .filter((i) => i.clase === "B")
    .map((i) => ({
      valor: String(i.fiduciante_id),
      etiqueta: `${i.nombre} (tiene ${formatParticipacion(i.porcentaje)})`,
    }));
}

/** Las unidades que se pueden adjudicar (disponibles y con % del fideicomiso) que todavía no se eligieron. */
export function opcionesUnidades(
  unidades: Unidad[],
  elegidas: number[],
): { valor: string; etiqueta: string }[] {
  return unidades
    .filter(
      (u) =>
        u.estado_venta === "disponible" &&
        u.porcentaje_fideicomiso !== null &&
        !elegidas.includes(u.id),
    )
    .map((u) => ({
      valor: String(u.id),
      etiqueta: `${nombreUnidad(u)} · ${formatParticipacion(u.porcentaje_fideicomiso)}`,
    }));
}

/** > 0 le falta (alguien cede), < 0 le sobra (alguien recibe), 0 justo. Escalado. */
export const diferenciaEscalada = (v: VistaPreviaAdjudicacion): number => {
  const d = v.diferencia.trim();
  const e = aEscalado(d.replace(/^-/, "")) ?? 0;
  return d.startsWith("-") ? -e : e;
};

const claveDe = (fiduciante_id: number, clase: ClaseFiduciante) =>
  `${fiduciante_id}-${clase}`;

/**
 * Quiénes pueden ceder (le falta: los ítems de la distribución vigente) o recibir (le sobra: cualquier fiduciante en
 * cualquier clase), menos el ítem B del adjudicatario y los ya agregados. Valor "id-clase".
 */
export function opcionesContrapartes(
  v: VistaPreviaAdjudicacion,
  fiduciantes: { id: number; nombre: string }[],
  form: FormAdjudicacion,
): { valor: string; etiqueta: string }[] {
  const usadas = new Set(
    form.contrapartes.map((c) => claveDe(c.fiduciante_id, c.clase)),
  );
  const propia = claveDe(form.fiduciante_id ?? -1, "B");
  const opciones =
    diferenciaEscalada(v) > 0
      ? v.items.map((i) => ({
          valor: claveDe(i.fiduciante_id, i.clase),
          etiqueta: `${i.nombre} · clase ${i.clase} (tiene ${formatParticipacion(i.porcentaje)})`,
        }))
      : fiduciantes.flatMap((f) =>
          (["A", "B"] as const).map((clase) => ({
            valor: claveDe(f.id, clase),
            etiqueta: `${f.nombre} · clase ${clase}`,
          })),
        );
  return opciones.filter((o) => o.valor !== propia && !usadas.has(o.valor));
}

export function parseClave(valor: string): {
  fiduciante_id: number;
  clase: ClaseFiduciante;
} {
  const [id, clase] = valor.split("-");
  return { fiduciante_id: Number(id), clase: clase as ClaseFiduciante };
}

/** Lo que falta repartir entre las contrapartes (escalado); null si algún % no es válido. */
export function queda(
  v: VistaPreviaAdjudicacion,
  contrapartes: Contraparte[],
): number | null {
  let total = 0;
  for (const c of contrapartes) {
    const e = aEscalado(c.porcentaje);
    if (e === null || e === 0) return null;
    total += e;
  }
  return Math.abs(diferenciaEscalada(v)) - total;
}

const pct = (escalado: number) => formatParticipacion(aTextoApi(escalado));

export function textoQueda(resto: number | null): string {
  if (resto === null) return "Revisá los porcentajes";
  if (resto === 0) return "Repartido todo";
  return resto > 0
    ? `Queda por repartir: ${pct(resto)}`
    : `Sobra ${pct(-resto)}`;
}

/** "Tiene 5 % · Las unidades suman 6,18 % · Le faltan 1,18 %: alguien tiene que cederlo". */
export function textoDiferencia(v: VistaPreviaAdjudicacion): string {
  const base = `Tiene ${formatParticipacion(v.porcentaje_anterior)} · Las unidades suman ${formatParticipacion(v.porcentaje_nuevo)}`;
  const d = diferenciaEscalada(v);
  if (d === 0) return `${base} · Justo: no cambia la participación`;
  return d > 0
    ? `${base} · Le faltan ${pct(d)}: alguien tiene que cederlo`
    : `${base} · Le sobran ${pct(-d)}: alguien los recibe`;
}

/** "Lo atrasado: USD 236,00 (1,18 % de USD 20.000,00 gastados antes del 01/06/2026). Lo paga Juan a los que ceden." */
export function textoAtrasado(
  v: VistaPreviaAdjudicacion,
  nombre: string,
  fecha: string,
): string | null {
  const d = diferenciaEscalada(v);
  if (d === 0) return null;
  const base = `Lo atrasado: ${formatMoneda(v.atrasado_usd, "USD")} (${pct(Math.abs(d))} de ${formatMoneda(v.base_usd, "USD")} gastados antes del ${formatFecha(fecha)}).`;
  return d > 0
    ? `${base} Lo paga ${nombre} a los que ceden.`
    : `${base} Le queda a favor a ${nombre}; lo pagan los que reciben.`;
}

export function aPayloadAdjudicacion(
  form: FormAdjudicacion,
): PayloadAdjudicacion {
  return {
    fiduciante_id: form.fiduciante_id ?? 0,
    unidad_ids: form.unidad_ids,
    fecha: form.fecha,
    contrapartes: form.contrapartes.map((c) => ({
      fiduciante_id: c.fiduciante_id,
      clase: c.clase,
      porcentaje: aTextoApi(aEscalado(c.porcentaje) ?? 0),
    })),
    nota: form.nota.trim() || null,
  };
}

/** "5 % → 6,18 %". */
export const textoCambio = (a: {
  porcentaje_anterior: string;
  porcentaje_nuevo: string;
}) =>
  `${formatParticipacion(a.porcentaje_anterior)} → ${formatParticipacion(a.porcentaje_nuevo)}`;
